import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  onSnapshot,
  writeBatch
} from 'firebase/firestore';
import { db } from './config';

// Notification types
export const NOTIFICATION_TYPES = {
  MESSAGE: 'message',
  PAYMENT: 'payment',
  MAINTENANCE: 'maintenance',
  APARTMENT: 'apartment',
  SYSTEM: 'system',
  LATE_FEE: 'late_fee',
  BACKUP: 'backup',
  RESTORE: 'restore'
};

// Notification priorities
export const NOTIFICATION_PRIORITIES = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent'
};

// Create a new notification
export const createNotification = async (notificationData) => {
  try {
    const notification = {
      ...notificationData,
      createdAt: serverTimestamp(),
      read: false
      // Remove the id: null - let Firestore generate the ID naturally
    };

    const docRef = await addDoc(collection(db, 'notifications'), notification);
    
    // Return the created notification with the ID
    return { 
      success: true, 
      notificationId: docRef.id,
      notification: {
        id: docRef.id,
        ...notification
      }
    };
  } catch (error) {
    console.error('Error creating notification:', error);
    return { success: false, error: error.message };
  }
};

// Get notifications for a specific user
export const getUserNotifications = async (userId, limit = 50) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limit)
    );
    
    const querySnapshot = await getDocs(q);
    const notifications = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, notifications };
  } catch (error) {
    console.error('Error fetching user notifications:', error);
    return { success: false, error: error.message };
  }
};

// Mark notification as read
export const markNotificationAsRead = async (notificationId) => {
  try {
    await updateDoc(doc(db, 'notifications', notificationId), {
      read: true,
      readAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return { success: false, error: error.message };
  }
};

// Mark all notifications as read for a user
export const markAllNotificationsAsRead = async (userId) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', userId),
      where('read', '==', false)
    );
    
    const querySnapshot = await getDocs(q);
    const batch = writeBatch(db);
    
    querySnapshot.docs.forEach(doc => {
      batch.update(doc.ref, {
        read: true,
        readAt: serverTimestamp()
      });
    });
    
    await batch.commit();
    
    return { success: true, updatedCount: querySnapshot.docs.length };
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    return { success: false, error: error.message };
  }
};

// Delete a notification
export const deleteNotification = async (notificationId) => {
  try {
    await deleteDoc(doc(db, 'notifications', notificationId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting notification:', error);
    return { success: false, error: error.message };
  }
};

// Get unread notification count for a user
export const getUnreadNotificationCount = async (userId) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', userId),
      where('read', '==', false)
    );
    
    const querySnapshot = await getDocs(q);
    return { success: true, count: querySnapshot.docs.length };
  } catch (error) {
    console.error('Error getting unread notification count:', error);
    return { success: false, error: error.message };
  }
};

// Real-time listener for notifications
export const subscribeToNotifications = (userId, callback) => {
  const q = query(
    collection(db, 'notifications'),
    where('recipientId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  
  return onSnapshot(q, (snapshot) => {
    const notifications = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(notification => notification.id && notification.id !== null); // Filter out notifications without IDs
    
    callback(notifications);
  });
};

// Real-time listener for unread count
export const subscribeToUnreadCount = (userId, callback) => {
  const q = query(
    collection(db, 'notifications'),
    where('recipientId', '==', userId),
    where('read', '==', false)
  );
  
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.length);
  });
};

// Notification creation helpers for different types
export const notificationHelpers = {
  // Message notification
  createMessageNotification: async (recipientId, senderId, senderName, messagePreview) => {
    return createNotification({
      type: NOTIFICATION_TYPES.MESSAGE,
      recipientId,
      title: 'New Message',
      message: `You have a new message from ${senderName}`,
      data: {
        senderId,
        senderName,
        messagePreview: messagePreview.substring(0, 100) + (messagePreview.length > 100 ? '...' : ''),
        action: 'view_message'
      },
      priority: NOTIFICATION_PRIORITIES.MEDIUM,
      icon: 'message'
    });
  },

  // Payment notification
  createPaymentNotification: async (recipientId, paymentData, type = 'received') => {
    const isAdmin = type === 'admin';
    const title = isAdmin ? 'New Payment Received' : 'Payment Status Updated';
    const message = isAdmin 
      ? `Payment of ₱${paymentData.amount} received from ${paymentData.tenantName}`
      : `Your payment of ₱${paymentData.amount} has been ${paymentData.status.toLowerCase()}`;

    // Extract receiptUrl if present
    let receiptUrl = undefined;
    if (paymentData.ewalletPaymentDetails && paymentData.ewalletPaymentDetails.receiptUrl) {
      receiptUrl = paymentData.ewalletPaymentDetails.receiptUrl;
    }

    return createNotification({
      type: NOTIFICATION_TYPES.PAYMENT,
      recipientId,
      title,
      message,
      data: {
        paymentId: paymentData.id,
        amount: paymentData.amount,
        status: paymentData.status,
        action: 'view_payment',
        senderId: paymentData.submittedBy || paymentData.tenantId, // Include sender ID for Firestore rules
        ...(receiptUrl ? { receiptUrl } : {})
      },
      priority: NOTIFICATION_PRIORITIES.HIGH,
      icon: 'payment'
    });
  },

  // Maintenance notification
  createMaintenanceNotification: async (recipientId, maintenanceData, type = 'request') => {
    const isAdmin = type === 'admin';
    const title = isAdmin ? 'New Maintenance Request' : 'Maintenance Update';
    const message = isAdmin
      ? `New maintenance request from ${maintenanceData.tenantName}`
      : `Your maintenance request has been ${maintenanceData.status.toLowerCase()}`;

    return createNotification({
      type: NOTIFICATION_TYPES.MAINTENANCE,
      recipientId,
      title,
      message,
      data: {
        maintenanceId: maintenanceData.id,
        issueType: maintenanceData.issueType,
        status: maintenanceData.status,
        action: 'view_maintenance'
      },
      priority: maintenanceData.priority === 'Emergency' ? NOTIFICATION_PRIORITIES.URGENT : NOTIFICATION_PRIORITIES.MEDIUM,
      icon: 'maintenance'
    });
  },

  // Late fee notification
  createLateFeeNotification: async (recipientId, lateFeeData) => {
    return createNotification({
      type: NOTIFICATION_TYPES.LATE_FEE,
      recipientId,
      title: 'Late Fee Applied',
      message: `A late fee of ₱${lateFeeData.amount} has been applied to your account`,
      data: {
        paymentId: lateFeeData.paymentId,
        amount: lateFeeData.amount,
        reason: lateFeeData.reason,
        action: 'view_payment'
      },
      priority: NOTIFICATION_PRIORITIES.HIGH,
      icon: 'warning'
    });
  },

  // System notification
  createSystemNotification: async (recipientId, title, message, priority = NOTIFICATION_PRIORITIES.MEDIUM) => {
    return createNotification({
      type: NOTIFICATION_TYPES.SYSTEM,
      recipientId,
      title,
      message,
      data: {
        action: 'system_message'
      },
      priority,
      icon: 'info'
    });
  },

  // Backup notification
  createBackupNotification: async (recipientId, backupData, type = 'created') => {
    const isSuccess = type === 'created' || type === 'completed';
    const isError = type === 'failed';
    
    let title, message, priority, icon;
    
    switch (type) {
      case 'created':
        title = 'Backup Created Successfully';
        message = `System backup "${backupData.name}" created with ${backupData.documentCount} documents`;
        priority = NOTIFICATION_PRIORITIES.MEDIUM;
        icon = 'backup';
        break;
      case 'failed':
        title = 'Backup Creation Failed';
        message = `Failed to create backup: ${backupData.error || 'Unknown error'}`;
        priority = NOTIFICATION_PRIORITIES.HIGH;
        icon = 'error';
        break;
      case 'deleted':
        title = 'Backup Deleted';
        message = `Backup "${backupData.name}" has been deleted`;
        priority = NOTIFICATION_PRIORITIES.LOW;
        icon = 'delete';
        break;
      case 'scheduled':
        title = 'Automatic Backup Scheduled';
        message = 'Daily automatic backup has been scheduled';
        priority = NOTIFICATION_PRIORITIES.LOW;
        icon = 'schedule';
        break;
      default:
        title = 'Backup Operation';
        message = 'A backup operation has been performed';
        priority = NOTIFICATION_PRIORITIES.MEDIUM;
        icon = 'backup';
    }

    return createNotification({
      type: NOTIFICATION_TYPES.BACKUP,
      recipientId,
      title,
      message,
      data: {
        backupId: backupData.id,
        backupName: backupData.name,
        documentCount: backupData.documentCount,
        action: 'view_backup',
        operationType: type,
        ...(backupData.error && { error: backupData.error })
      },
      priority,
      icon
    });
  },

  // Restore notification
  createRestoreNotification: async (recipientId, restoreData, type = 'completed') => {
    const isSuccess = type === 'completed';
    const isError = type === 'failed';
    
    let title, message, priority, icon;
    
    switch (type) {
      case 'started':
        title = 'System Restore Started';
        message = `Restoring system from backup "${restoreData.backupName}"`;
        priority = NOTIFICATION_PRIORITIES.HIGH;
        icon = 'restore';
        break;
      case 'completed':
        title = 'System Restore Completed';
        message = `Successfully restored ${restoreData.totalRestored} documents from backup "${restoreData.backupName}"`;
        priority = NOTIFICATION_PRIORITIES.HIGH;
        icon = 'check_circle';
        break;
      case 'failed':
        title = 'System Restore Failed';
        message = `Failed to restore from backup: ${restoreData.error || 'Unknown error'}`;
        priority = NOTIFICATION_PRIORITIES.URGENT;
        icon = 'error';
        break;
      default:
        title = 'Restore Operation';
        message = 'A restore operation has been performed';
        priority = NOTIFICATION_PRIORITIES.HIGH;
        icon = 'restore';
    }

    return createNotification({
      type: NOTIFICATION_TYPES.RESTORE,
      recipientId,
      title,
      message,
      data: {
        backupId: restoreData.backupId,
        backupName: restoreData.backupName,
        totalRestored: restoreData.totalRestored,
        action: 'view_restore',
        operationType: type,
        ...(restoreData.error && { error: restoreData.error })
      },
      priority,
      icon
    });
  }
};

// Auto-create notifications for database changes
export const autoCreateNotifications = {
  // Called when a new message is sent
  onMessageSent: async (messageData) => {
    const recipientId = messageData.receiverId;
    const senderName = messageData.senderName;
    const messagePreview = messageData.content;
    
    return notificationHelpers.createMessageNotification(
      recipientId,
      messageData.senderId,
      senderName,
      messagePreview
    );
  },

  // Called when payment status changes
  onPaymentUpdate: async (paymentData, previousStatus) => {
    const notifications = [];
    
    // Notify tenant about payment status change
    if (paymentData.tenantId) {
      const tenantNotification = await notificationHelpers.createPaymentNotification(
        paymentData.tenantId,
        paymentData,
        'tenant'
      );
      if (tenantNotification.success) {
        notifications.push(tenantNotification);
      }
    }
    
    // Notify admin about payment status change to Paid, Overdue, or Cancelled
    const notifyAdminStatuses = ['Paid', 'Overdue', 'Cancelled'];
    if (notifyAdminStatuses.includes(paymentData.status) && previousStatus !== paymentData.status) {
      // Get all admin users
      const adminQuery = query(collection(db, 'users'), where('role', '==', 'admin'));
      const adminSnapshot = await getDocs(adminQuery);
      
      for (const adminDoc of adminSnapshot.docs) {
        const adminNotification = await notificationHelpers.createPaymentNotification(
          adminDoc.data().uid,
          paymentData,
          'admin'
        );
        if (adminNotification.success) {
          notifications.push(adminNotification);
        }
      }
    }
    
    return notifications;
  },

  // Called when maintenance request is created or updated
  onMaintenanceUpdate: async (maintenanceData, previousStatus) => {
    const notifications = [];
    
    // Notify tenant about maintenance status change
    if (maintenanceData.tenantId) {
      const tenantNotification = await notificationHelpers.createMaintenanceNotification(
        maintenanceData.tenantId,
        maintenanceData,
        'tenant'
      );
      if (tenantNotification.success) {
        notifications.push(tenantNotification);
      }
    }
    
    // Notify admin about new maintenance request
    if (maintenanceData.status === 'Pending' && previousStatus !== 'Pending') {
      // Get all admin users
      const adminQuery = query(collection(db, 'users'), where('role', '==', 'admin'));
      const adminSnapshot = await getDocs(adminQuery);
      
      for (const adminDoc of adminSnapshot.docs) {
        const adminNotification = await notificationHelpers.createMaintenanceNotification(
          adminDoc.data().uid,
          maintenanceData,
          'admin'
        );
        if (adminNotification.success) {
          notifications.push(adminNotification);
        }
      }
    }
    
    return notifications;
  },

  // Called when late fee is applied
  onLateFeeApplied: async (lateFeeData) => {
    return notificationHelpers.createLateFeeNotification(
      lateFeeData.tenantId,
      lateFeeData
    );
  },

  // Called when backup operation is performed
  onBackupOperation: async (backupData, operationType = 'created') => {
    const notifications = [];
    
    // Get all admin users to notify
    const adminQuery = query(collection(db, 'users'), where('role', '==', 'admin'));
    const adminSnapshot = await getDocs(adminQuery);
    
    for (const adminDoc of adminSnapshot.docs) {
      const adminNotification = await notificationHelpers.createBackupNotification(
        adminDoc.data().uid,
        backupData,
        operationType
      );
      if (adminNotification.success) {
        notifications.push(adminNotification);
      }
    }
    
    return notifications;
  },

  // Called when restore operation is performed
  onRestoreOperation: async (restoreData, operationType = 'completed') => {
    const notifications = [];
    
    // Get all admin users to notify
    const adminQuery = query(collection(db, 'users'), where('role', '==', 'admin'));
    const adminSnapshot = await getDocs(adminQuery);
    
    for (const adminDoc of adminSnapshot.docs) {
      const adminNotification = await notificationHelpers.createRestoreNotification(
        adminDoc.data().uid,
        restoreData,
        operationType
      );
      if (adminNotification.success) {
        notifications.push(adminNotification);
      }
    }
    
    return notifications;
  }
}; 